<template>
    <div>
        <div class="finance-quote__container">
            <template v-if="loading">
                <div class="finance-quote__summary">
                    <div class="loading"><img src="/images/shared/Robins-And-Day-Loader.svg" alt="Loading" /></div>
                </div>
            </template>
            <template v-else-if="!loading && (!quote || quote.HasError == true)">
                <div :class="{'finance-quote__hide-copy': submitted }" class="finance-quote__summary">
                    <h3 class="finance-quote__heading">Request Quote from Dealer</h3>

                    <p>We are unable to offer a personalised quote on this finance product. Please fill in the form and we'll be in touch soon.</p>
                </div>
                <div class="finance-quote__details">
                    <manual-quote-form @submitted="formCompleted" :vehicle="vehicle" :quote-type="quoteType" :term="term" :deposit="deposit" :mileage="mileage"></manual-quote-form>
                </div>
            </template>
            <template v-else>
                <div class="finance-quote__summary">
                    <span class="text-center finance-quote__heading" v-if="modified">Your Personal Quote*</span>
                    <span class="text-center finance-quote__heading" v-else>Representative Example*</span>

                    <div class="finance-quote__monthly-payment-box">
                        <span class="finance-quote__monthly-payment">&pound;{{ numberFormat(quote.Quote.AllInclusiveRegularPayment, 2) }}</span>
                        x {{ numberOfPayments }} months
                    </div>
                </div>
                <div class="finance-quote__details">
                    <QuotePcp v-if="quoteType == financeKeys.PCP || quoteType == financeKeys.OVERAGE_PCP_ONLY" :brand-p-c-p-finance-name="brandPCPFinanceName" :loading="loading" :vehicle="vehicle" :quote="quote" />
                    <QuotePsaPcpOnly v-else-if="quoteType == financeKeys.PSA_PCP_ONLY || quoteType == financeKeys.VAUXHALL_PCP_ONLY" :loading="loading" :quote="quote" :vehicle="vehicle" />
                    <QuoteDsgPcpOnly v-else-if="quoteType == financeKeys.DSG_PCP_ONLY" :loading="loading" :quote="quote" :vehicle="vehicle"></QuoteDsgPcpOnly>
                    <QuoteHp v-else-if="quoteType == financeKeys.HP" :loading="loading" :quote="quote" :vehicle="vehicle" />
                </div>
                <div class="finance-quote__ctas" v-if="vehicle.is_new == 0">
                    <router-link v-if="!applyOnlineDisabled" :to="freeCreditCheckLink" class="btn btn--primary-inverted btn--margin-bottom btn--full-width" tag="a" :class="{ 'btn--disabled': loading }">Free Credit Check</router-link>
                    <router-link v-if="!applyOnlineDisabled" :to="applyForFinanceLink" class="btn btn--primary btn--full-width" tag="a" :class="{ 'btn--disabled': loading }">Apply For Finance</router-link>
                </div>
            </template>
        </div>
    </div>
</template>

<script>
import FinanceCallToActions from './FinanceCallToActions';
import FinanceAdjustmentNotifications from './FinanceAdjustmentNotifications.vue';
import numberFormat from '../../api/NumberFormat';
import { financeKeys } from '../../../modules/json/financeKeys';
import ManualQuoteForm from './Quotes/ManualQuoteForm.vue';
import QuotePcp from './Quotes/Quote-PCP.vue';
import QuotePsaPcpOnly from './Quotes/Quote-PSA_PCP_ONLY.vue';
import QuoteDsgPcpOnly from './Quotes/Quote-DSG_PCP_ONLY.vue';
import QuoteHp from './Quotes/Quote-HP.vue';
import { generateLeadEvent } from '../../../modules/tracking/AnalyticsEvents';

export default {
    props: {
        loading: {
            required: true,
        },
        vehicle: {
            required: true,
        },
        quote: {
            required: true,
        },
        term: {
            required: true,
        },
        termChanged: {
            required: true,
        },
        deposit: {
            required: true,
        },
        mileage: {
            required: true,
        },
        mileageChanged: {
            required: true,
        },
        brandPCPFinanceName: {
            required: true,
        },
        applyOnlineDisabled: {
            required: false,
            default: false,
        }
    },
    data() {
        return {
            submitted:null,
            showValuationChoiceBox: false,
            financeKeys: financeKeys,
        };
    },
    components: {
        FinanceAdjustmentNotifications,
        FinanceCallToActions,
        ManualQuoteForm,
        QuotePcp,
        QuotePsaPcpOnly,
        QuoteDsgPcpOnly,
        QuoteHp,
    },
    methods: {
        formCompleted(submitted){
            this.submitted =  submitted
        },
        numberFormat(num, decimals) {
            return numberFormat(num, decimals);
        },
        freeCreditCheckEvent() {
            generateLeadEvent('stock_search_credit_check');
        },
        applyForFinanceEvent() {
            generateLeadEvent('stock_search_finance_application');
        },
    },
    computed: {
        modified() {
            if (this.quote.repExample) {
                return false;
            }
            return this.$store.state.FinanceParameters.modified;
        },
        numberOfPayments() {
            const paymentCount = this.quote.Quote.Payments.length || 0;
            if (paymentCount == 0) {
                if (this.quote.Quote.Term) {
                    return quote.Quote.Term;
                }
                return 0;
            }
            if (paymentCount == 1 && this.quote.Quote.Payments && this.quote.Quote.Payments[0] && this.quote.Quote.Payments[0].NumberOfPayments) {
                return parseInt(this.quote.Quote.Payments[0].NumberOfPayments);
            }
            let numberOfPayments = 0;
            for (let index=0, len=(paymentCount - 1); index < len; index++) {
                if (this.quote.Quote.Payments[index].NumberOfPayments) {
                    numberOfPayments = numberOfPayments + parseInt(this.quote.Quote.Payments[index].NumberOfPayments);
                }
            }
            return numberOfPayments;
        },
        quoteType() {
            if (typeof this.quote.Key !== 'undefined') {
                return this.quote.Key;
            }
            return false;
        },
        todayString() {
            const today = new Date();
            const day = String(today.getDate()).padStart(2, '0');
            const month = String(today.getMonth() + 1).padStart(2, '0'); //January is 0!
            const year = today.getFullYear();

            return day + '-' + month + '-' + year;
        },
        dealerName() {
            if (this.vehicle && this.vehicle.location && this.vehicle.location.name) {
                return this.vehicle.location.name;
            }
            return 'the dealer';
        },
        freeCreditCheckLink() {
            if (this.loading) {
                return { name: 'finance-application', params: { applicationType: 'soft', id: this.vehicle.id, quoteRef: 'none' } };
            }
            return { name: 'finance-application', params: { applicationType: 'soft', id: this.vehicle.id, quoteRef: this.quote.Quote.QuoteReference } }
        },
        applyForFinanceLink() {
            if (this.loading) {
                return { name: 'finance-application', params: { applicationType: 'full', id: this.vehicle.id, quoteRef: 'none' } };
            }
            return { name: 'finance-application', params: { applicationType: 'full', id: this.vehicle.id, quoteRef: this.quote.Quote.QuoteReference } }
        },
    }
}
</script>

<style lang="scss" scoped>
@import "../../../../sass/redesign/variables";

.finance-quote {
    &__heading {
        font-weight: 700;
        font-size: 18px;
        line-height: 21px;
    }

    &__monthly-payment-box {
        font-weight: 400;
        font-size: 16px;
        line-height: 20px;
        margin: 17px 0;
        text-align: center;

        @media (min-width: $screen-sm) {
            text-align: left;
        }
    }

    &__monthly-payment {
        font-weight: 800;
        font-size: 22px;
        line-height: 20px;
        color: $blue-price;
    }

    &__ctas {
        margin-top: 17px;
        display: flex;
        flex-direction: column;
        gap: 15px;
    }
    &__hide-copy {
        display: none;
    }
}
</style>


// WEBPACK FOOTER //
// resources/assets/js/used-vue/components/Finance/FinanceQuote.vue